import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import {
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Typography
} from "@material-ui/core";

import { Formik } from "formik";
import * as Yup from "yup";
import { Autocomplete } from "@material-ui/lab";
import { withAlert } from "react-alert";
import styled from "styled-components";
import { withTranslation } from "react-i18next";
import SupportService from "../../services/SupportService";
import Button from "../../waybee-ui/Button";
import Group from "../../waybee-ui/Group";
import Heading from "../../waybee-ui/Heading";
import Dialog from "../../waybee-ui/Dialog";
import isClassDiaryDemo from "../../demo/isClassDiaryDemo";
import SupportList from "./components/SupportList";

const validationSchema = Yup.object().shape({
  typeId: Yup.string().required(),
  reasonId: Yup.string().required(),
  location: Yup.string().required(),
  description: Yup.string()
});

const LoadContainer = styled.div`
  text-align: center;
  margin: 8px;
`;

class Support extends Component {
  constructor() {
    super();
    this.state = {
      loading: false,
      openDialog: false,
      supportTypes: null,
      supportReasons: null,
      supportClasses: null,
      supports: [],
      filters: {
        searchText: "",
        initialDate: null,
        finalDate: null
      }
    };
  }

  componentDidMount() {
    this.bootstrap();
  }

  applyDemoSupportFilters = supports => {
    if (!isClassDiaryDemo()) return supports;
    const { filters } = this.state;
    let out = [...supports];
    if (filters.searchText && filters.searchText.length >= 3) {
      const q = filters.searchText.toLowerCase();
      out = out.filter(
        s =>
          (s.description && s.description.toLowerCase().includes(q)) ||
          (s.SupportReason &&
            s.SupportReason.name &&
            s.SupportReason.name.toLowerCase().includes(q)) ||
          (s.SupportReason &&
            s.SupportReason.SupportType &&
            s.SupportReason.SupportType.name &&
            s.SupportReason.SupportType.name.toLowerCase().includes(q))
      );
    }
    if (filters.initialDate) {
      const start = new Date(filters.initialDate);
      start.setHours(0, 0, 0, 0);
      out = out.filter(s => new Date(s.createdAt) >= start);
    }
    if (filters.finalDate) {
      const end = new Date(filters.finalDate);
      end.setHours(23, 59, 59, 999);
      out = out.filter(s => new Date(s.createdAt) <= end);
    }
    return out;
  };

  bootstrap = async () => {
    this.setState({ loading: true });
    try {
      const [
        supportTypes,
        supportReasons,
        supportClasses,
        supportsRaw
      ] = await Promise.all([
        SupportService.getSupportTypes(),
        SupportService.getSupportReasons(),
        SupportService.getSupportClasses(),
        SupportService.getSupports(0, "", undefined, undefined)
      ]);
      let supports = Array.isArray(supportsRaw) ? supportsRaw : [];
      if (isClassDiaryDemo()) {
        supports = this.applyDemoSupportFilters(supports);
      }
      this.setState({
        supportTypes,
        supportReasons,
        supportClasses,
        supports
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  loadSupports = async () => {
    const { filters } = this.state;
    const data = await SupportService.getSupports(
      0,
      filters.searchText || "",
      filters.initialDate,
      filters.finalDate
    );
    let supports = Array.isArray(data) ? data : [];
    if (isClassDiaryDemo()) {
      supports = this.applyDemoSupportFilters(supports);
    }
    this.setState({ supports });
  };

  setFilter = (key, value) => {
    this.setState(
      prev => ({
        filters: { ...prev.filters, [key]: value }
      }),
      () => this.loadSupports()
    );
  };

  onSelectSupport = support => {
    const { alert, t } = this.props;
    const desc = support.description || t("support.noNotes");
    alert.show(`${desc}`, {
      title: t("support.ticketTitle", {
        id: support.id,
        reason: support.SupportReason.name
      })
    });
  };

  closeDialog = () => {
    this.setState({ openDialog: false });
  };

  createSupport = values => {
    const { alert, t } = this.props;
    this.setState({ loading: true });
    return SupportService.createSupport(values)
      .then(async () => {
        alert.show(t("support.sent"), { title: t("support.sentTitle") });
        await this.loadSupports();
        this.setState({ openDialog: false });
      })
      .catch(e => {
        alert.show(e.message, {
          title: t("support.sendError")
        });
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  renderFormFields = ({
    values,
    setFieldValue,
    errors,
    handleChange,
    handleSubmit,
    supportTypes,
    supportReasons,
    supportClasses,
    loading,
    t
  }) => (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            select
            label={t("common.labels.type")}
            name="typeId"
            fullWidth
            value={values.typeId || " "}
            onChange={handleChange}
            error={Boolean(errors.typeId)}
            helperText={errors.typeId}
          >
            {supportTypes &&
              supportTypes.map(type => (
                <MenuItem key={type.CODIGO} value={type.MOTIVO}>
                  {type.MOTIVO}
                </MenuItem>
              ))}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField
            select
            name="reasonId"
            label={t("support.reason")}
            value={values.reasonId || " "}
            onChange={handleChange}
            fullWidth
            error={Boolean(errors.reasonId)}
            helperText={errors.reasonId}
          >
            {supportReasons &&
              supportReasons.map(reason => (
                <MenuItem key={reason.CODIGO} value={reason.MOTIVO}>
                  {reason.MOTIVO}
                </MenuItem>
              ))}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            id="location"
            name="location"
            fullWidth
            disablePortal
            options={supportClasses || []}
            getOptionLabel={options => options.DESCRICAO}
            value={
              (supportClasses || []).find(
                o => o.DESCRICAO === values.location
              ) || null
            }
            onChange={(event, locationValue) => {
              if (!locationValue) {
                setFieldValue("location", null);
              } else {
                setFieldValue("location", locationValue.DESCRICAO);
              }
            }}
            renderInput={params => (
              <TextField
                {...params}
                label={t("support.room")}
                error={Boolean(errors.location)}
                helperText={errors.location}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body1" color="textSecondary" gutterBottom>
            {t("common.labels.notesOptional")}
          </Typography>
          <TextField
            multiline
            rows={4}
            variant="outlined"
            placeholder={t("support.notesPlaceholder")}
            onChange={handleChange}
            fullWidth
            name="description"
            value={values.description || ""}
            error={Boolean(errors.description)}
            helperText={errors.description}
          />
        </Grid>
        <Grid item xs={12} container justify="flex-end">
          <Button
            variant="contained"
            disabled={loading}
            onClick={handleSubmit}
            color="primary"
          >
            {t("common.actions.send")}
          </Button>
        </Grid>
      </Grid>
    </>
  );

  render() {
    const {
      loading,
      openDialog,
      supportTypes,
      supportReasons,
      supportClasses,
      supports,
      filters
    } = this.state;
    const { t } = this.props;

    if (loading && !supportTypes)
      return (
        <LoadContainer>
          <CircularProgress />
        </LoadContainer>
      );

    return (
      <>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Group>
              <Box>
                <Box
                  display="flex"
                  flexWrap="wrap"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={2}
                  style={{ gap: 16 }}
                >
                  <Heading level={1} icon="history" gutterBottom>
                    {t("support.openTickets")}
                  </Heading>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => this.setState({ openDialog: true })}
                  >
                    {t("support.new")}
                  </Button>
                </Box>
                <SupportList
                  supports={supports}
                  filters={filters}
                  setFilter={this.setFilter}
                  onSelectSupport={this.onSelectSupport}
                />
              </Box>
            </Group>
          </Grid>
        </Grid>

        <Dialog
          open={openDialog}
          onClose={this.closeDialog}
          closeButton
          maxWidth="sm"
          fullWidth
          loading={loading}
        >
          <Box p={3} pt={4}>
            <Heading level={2} icon="error_icon" gutterBottom>
              {t("support.newDialog")}
            </Heading>
            <Box pt={2}>
              <Formik
                validateOnBlur
                isInitialValid={false}
                validateOnChange={false}
                initialValues={{}}
                enableReinitialize
                onSubmit={(values, actions) => {
                  this.createSupport(values).then(() => {
                    actions.resetForm();
                  });
                }}
                validationSchema={validationSchema}
              >
                {({
                  values,
                  setFieldValue,
                  errors,
                  handleChange,
                  handleSubmit
                }) =>
                  this.renderFormFields({
                    values,
                    setFieldValue,
                    errors,
                    handleChange,
                    handleSubmit,
                    supportTypes,
                    supportReasons,
                    supportClasses,
                    loading,
                    t
                  })}
              </Formik>
            </Box>
          </Box>
        </Dialog>
      </>
    );
  }
}

Support.propTypes = {
  alert: PropTypes.shape({
    show: PropTypes.func
  }).isRequired,
  t: PropTypes.func.isRequired
};

export default withAlert()(withTranslation()(Support));
